import { useCallback, useEffect, useMemo, useRef } from "react";

import * as audienceApi from "@/api";
import { useAudienceActions } from "@/context";
import { useAudienceCache, useClient, useServices } from "@/hooks";
import { Audience, BuildAudience, SiteAudience, UploadAudience } from "@/types";
import {
  audiencePath,
  buildGeoFence,
  buildQueries,
  checkAudienceSize,
  extractSegments,
  filterCatchment,
  modeThreshold,
  unwrap,
} from "@/utils";
import { useAudienceMutations } from "./useAudienceMutations";
import { TaskContext, useAudienceTask } from "./useAudienceTask";

const isUploadReady = (audience: UploadAudience): audience is UploadAudience & { upload_file: File; upload_mode: string } =>
  !!audience.upload_mode && audience.upload_file instanceof File;

const EMPTY_SIZE = { audience_geo: 'sector', audience_size: undefined };

/**
 * Everything the audience detail pages do: build, save, insight and export,
 * plus the audience mutations. All of it shares one action state.
 */
export const useAudienceWorkflow = (audienceId?: string, audience?: Audience) => {
  const { nav, ui } = useServices();
  const audienceActions = useAudienceActions();
  const client = useClient();
  const cache = useAudienceCache();
  const task = useAudienceTask();
  const { action, run, reset, isRunning, hasFailed } = task;
  const mutations = useAudienceMutations(audience, task);

  // ---- Build ----

  const buildBuildAudience = useCallback(async (aud: BuildAudience, { token }: TaskContext) => {
    // Update the audience state to be the latest audience
    audienceActions.load(aud);

    const data = unwrap(await audienceApi.buildAudience(
      token,
      buildQueries(aud),
      modeThreshold[aud.build_mode],
      aud.operator,
      buildGeoFence(aud),
      audienceId
    ));

    if (!checkAudienceSize(data.audience_size[0])) {
      throw new Error("Unable to build audience or the audience size was 0, please adjust your criteria and try again");
    }
    audienceActions.built(data);
  }, [audienceId, audienceActions]);

  const buildUploadAudience = useCallback(async (
    aud: UploadAudience & { upload_file: File; upload_mode: string },
    { token }: TaskContext
  ) => {
    // Update the audience state to be the latest audience
    audienceActions.load(aud);

    const data = unwrap(await audienceApi.buildUpload(
      token,
      aud.upload_file,
      aud.upload_mode,
      audienceId,
      aud.travel_time
    ));

    if (!data.audience_size?.some(size => checkAudienceSize(size))) {
      throw new Error("No valid geos were found in your file, please check the file contents and try again");
    }
    audienceActions.built(data);

    if (!data.audience_size.every(size => checkAudienceSize(size))) {
      throw new Error("One or more of your segments has no valid geos, please check the file contents and try again");
    }
    if (data.audience_size.length > 1) {
      audienceActions.addSegments(extractSegments(data.audience_size));
    }
  }, [audienceId, audienceActions]);

  // Site requests can overlap, as the page isn't locked while they run. Each
  // kind is numbered so only the latest request's response is used.
  const catchmentRequest = useRef(0);
  const sizeRequest = useRef(0);

  // The latest audience, for the site filters when a catchment finishes loading
  const latestAudience = useRef(audience);
  useEffect(() => { latestAudience.current = audience; }, [audience]);

  const sizeSite = useCallback(async (sectors: string[]) => {
    const request = ++sizeRequest.current;
    const isLatest = () => request === sizeRequest.current;

    await run('build', 'Building Site Audience...', async ({ token }) => {
      const response = await audienceApi.getCatchmentSize(token, sectors, audienceId);
      if (!isLatest()) return;

      const data = unwrap(response);
      if (!checkAudienceSize(data.audience_size[0])) {
        throw new Error("Unable to build audience or the audience size was 0, please adjust your criteria and try again");
      }
      audienceActions.built(data);
    });
  }, [audienceId, audienceActions, run]);

  /** Builds a build or upload audience. Site audiences are built from sectors with `buildSite`. */
  const build = useCallback(async () => {
    if (audience?.audience_type === 'build') {
      await run('build', 'Building Audience...', (context) => buildBuildAudience(audience, context));
    } else if (audience?.audience_type === 'upload' && isUploadReady(audience)) {
      await run('build', 'Building Upload...', (context) => buildUploadAudience(audience, context));
    }
  }, [audience, buildBuildAudience, buildUploadAudience, run]);

  /** Re-sizes a site audience's loaded catchment with new category and day type filters */
  const buildSite = useCallback(async (categories: string[], dayType: string) => {
    if (audience?.audience_type !== 'site') return;

    const sectors = filterCatchment(audience.site_config.catchment ?? [], categories, dayType)
      .map(x => x.sector);

    if (sectors.length > 0) {
      await sizeSite(sectors);
    } else {
      sizeRequest.current++; // Ignore any size request still running
      audienceActions.built(EMPTY_SIZE);
    }
  }, [audience, audienceActions, sizeSite]);

  /** Loads the catchment for the selected sites, then builds from the sectors in the chosen categories */
  const loadCatchment = useCallback(async (siteIds: number[], categories: string[], dayType: string) => {
    if (audience?.audience_type !== 'site') return;

    const request = ++catchmentRequest.current;
    const isLatest = () => request === catchmentRequest.current;

    if (siteIds.length === 0 || categories.length === 0) {
      sizeRequest.current++; // Ignore any size request still running
      audienceActions.setCatchment([]);
      audienceActions.built(EMPTY_SIZE);
      return;
    }

    let sectors: string[] = [];

    await run('catchment', 'Loading Site Catchment...', async ({ token }) => {
      const response = await audienceApi.getCatchment(token, siteIds, client);
      if (!isLatest()) return;

      const catchment = unwrap(response);
      if (!catchment) return;

      audienceActions.setCatchment(catchment);

      // Use the latest filters, as they may have changed while the catchment loaded
      const latest = latestAudience.current;
      const config = latest?.audience_type === 'site' ? latest.site_config : undefined;
      sectors = filterCatchment(catchment, config?.categories ?? categories, config?.day_type ?? dayType)
        .map(x => x.sector);

      if (sectors.length === 0) {
        sizeRequest.current++;
        audienceActions.built(EMPTY_SIZE);
      }
    });

    // Build as its own action once the catchment has loaded, so a build error
    // isn't overwritten by the catchment succeeding
    if (sectors.length > 0 && isLatest()) {
      await sizeSite(sectors);
    }
  }, [audience, client, audienceActions, run, sizeSite]);

  const validateFile = useCallback(async (file: File, mode: string) => {
    await run('check', 'Checking File...', async ({ token }) => {
      const { data, error } = await audienceApi.validateFile(token, file, mode);

      // An invalid file is a result, not a failure of the check
      audienceActions.setFileCheck(error ? { format: false, segment: false, error: true } : data);
    });
  }, [audienceActions, run]);

  // ---- Save ----

  // Calculates the insight for a saved audience, then goes to the insight page
  const completeAudience = useCallback(async (
    saved: Audience,
    notification: { message: string; category: string },
    { token, setMessage }: TaskContext
  ) => {
    setMessage("Calculating Insight...");
    const insight = unwrap(await audienceApi.getInsight(token, saved.audience_id, true, saved.client));
    cache.setInsight(saved.audience_id, insight);

    unwrap(await audienceApi.createNotification(token, {
      client: saved.client,
      message: notification.message,
      category: notification.category,
      data: { audience_id: saved.audience_id },
    }));

    unwrap(await audienceApi.updateAudienceStatus(token, saved.audience_id, 'complete'));

    nav.navigate(audiencePath(saved, "insight"));
  }, [cache, nav]);

  // Saves the audience and updates the context and cache with the saved version
  const saveToApi = useCallback(async (updated: Audience, token: string) => {
    const audiences = unwrap(await audienceApi.saveAudience(token, updated, true));
    const saved = audiences.filter((a) => a.audience_name === updated.audience_name)[0];

    audienceActions.saved(saved);
    cache.saved(saved.audience_id);
    return saved;
  }, [cache, audienceActions]);

  const saveAudience = useCallback(async (
    aud: BuildAudience | SiteAudience,
    audienceName: string,
    projectName: string | null
  ) => {
    const label = aud.audience_type === 'site' ? "Site Audience" : "Audience";

    await run('insight', `Saving ${label}...`, async (context) => {
      ui.closeDialog();

      const saved = await saveToApi({
        ...aud,
        audience_name: audienceName ?? aud.audience_name,
        project_name: projectName ?? aud.project_name,
      }, context.token);

      await completeAudience(saved, {
        message: `${label} "${saved.audience_name}" successfully created`,
        category: "Audience",
      }, context);
    });
  }, [completeAudience, run, saveToApi, ui]);

  // Requests the travel time catchment for a saved upload, then goes to the
  // audience page while it's processed
  const requestTravelTime = useCallback(async (
    original: UploadAudience,
    saved: Audience,
    travelTime: NonNullable<UploadAudience["travel_time"]>,
    isNew: boolean
  ) => {
    await run('traveltime', 'Requesting Travel Time Catchment...', async ({ token }) => {
      const response = await audienceApi.requestTravelTime(token, {
        ...travelTime,
        audience_id: saved.audience_id,
        audience_name: saved.audience_name,
        client
      });

      // A new audience is deleted so it isn't left processing forever, and the
      // unsaved audience is restored so it can be tried again. An existing
      // audience is never deleted.
      if (response.error && isNew) {
        const { error } = await audienceApi.deleteAudience(
          token,
          saved.audience_name,
          saved.audience_id,
          saved.audience_type,
          client
        );
        if (!error) cache.remove(saved.audience_id);
        audienceActions.load(original);
      }
      const traveltime = unwrap(response);

      nav.navigate(audiencePath({ audience_type: 'upload', audience_id: traveltime.audience_id }));
    });
  }, [cache, client, audienceActions, nav, run]);

  const saveUpload = useCallback(async (
    aud: UploadAudience & { upload_file: File; upload_mode: string },
    audienceName: string,
    projectName: string | null
  ) => {
    const travelTime = aud.upload_mode === 'travel_time' ? aud.travel_time : undefined;
    let saved: Audience | undefined;

    await run('insight', 'Uploading Data...', async (context) => {
      const { token } = context;
      ui.closeDialog();

      // 1. Upload the file
      const uploaded = unwrap(await audienceApi.upload(
        token,
        aud.upload_file,
        aud.upload_mode,
        audienceId,
        aud.travel_time
      ));

      // 2. Save the audience
      saved = await saveToApi({
        ...aud,
        audience_id: uploaded.audience_id,
        audience_name: audienceName ?? aud.audience_name,
        project_name: projectName ?? aud.project_name,
        upload_file: { name: aud.upload_file.name },
        status: "processing",
      }, token);

      // 3. Calculate the insight, unless it waits for the travel time catchment
      if (!travelTime) {
        await completeAudience(saved, {
          message: `Upload "${saved.audience_name}" successfully created`,
          category: "Upload",
        }, context);
      }
    });

    // Its own action, so a failure shows as a travel time error
    if (travelTime && saved) {
      await requestTravelTime(aud, saved, travelTime, !audienceId);
    }
  }, [audienceId, completeAudience, requestTravelTime, run, saveToApi, ui]);

  /** Saves the audience, calculates its insight and goes to the insight page */
  const save = useCallback(async (audienceName: string, projectName: string | null) => {
    if (!audience) return;

    if (audience.audience_type === 'upload') {
      if (isUploadReady(audience)) await saveUpload(audience, audienceName, projectName);
    } else if (audience.audience_type === 'build' || audience.audience_type === 'site') {
      await saveAudience(audience, audienceName, projectName);
    }
  }, [audience, saveAudience, saveUpload]);

  // ---- Insight & export ----

  /**
   * Opens the insight. New audiences are saved first, and saved audiences with
   * a stale insight are overwritten once the user confirms.
   */
  const openInsight = useCallback((isStale: boolean) => {
    if (!audience) return;

    if (!audience.audience_id) {
      ui.openDialog({
        type: "createAudience",
        props: { audienceType: audience.audience_type, onConfirm: save },
      });
    } else if (isStale) {
      ui.openDialog({
        type: "confirm",
        props: {
          message: `\nProceeding will mean that your saved ${audience.audience_type} audience will get overwritten.\nCreate a new audience if you don't want this to happen.\n`,
          onConfirm: () => save(audience.audience_name, audience.project_name),
        },
      });
    } else {
      nav.navigate(audiencePath(audience, "insight"));
    }
  }, [audience, nav, save, ui]);

  const openExport = useCallback(() => {
    if (!audience) return;
    nav.navigate(audiencePath(audience, "export"));
  }, [audience, nav]);

  const { rename, remove, share, unshare, clone } = mutations;

  return useMemo(() => ({
    build,
    buildSite,
    loadCatchment,
    validateFile,
    save,
    openInsight,
    openExport,
    rename,
    remove,
    share,
    unshare,
    clone,
    action,
    reset,
    isRunning,
    hasFailed,
  }), [
    build, buildSite, loadCatchment, validateFile, save, openInsight, openExport,
    rename, remove, share, unshare, clone,
    action, reset, isRunning, hasFailed,
  ]);
}

export type AudienceWorkflow = ReturnType<typeof useAudienceWorkflow>;
